import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  input,
  output,
  inject,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  CouponService,
  ExcelService,
  ServiceCategoryService,
} from '@fitness-force/api';
import { Status } from '@fitness-force/enum';
import type {
  AddServiceCategoryRequest,
  CouponServicesResponse,
  OfferMaster,
} from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastMessageService } from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ScrollPanelModule } from 'primeng/scrollpanel';

import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-coupon-service-restriction',
  templateUrl: './coupon-service-restriction.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastMessageService],
  standalone: true,
  imports: [
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollPanelModule,
    ButtonModule,
    SelectModule,
    TranslateModule,
    AsyncPipe,
  ],
})
export class CouponServiceRestrictionComponent implements OnInit {
  private coupnService = inject(CouponService);
  private translate = inject(TranslateService);
  private excelService = inject(ExcelService);
  private toastMessageService = inject(ToastMessageService);
  private serviceCategoryService = inject(ServiceCategoryService);

  readonly tenantId = input(0);
  readonly couponMaster = input<OfferMaster>();
  readonly visible = input<boolean>(false);
  readonly serviceIds = input<number[]>([], { alias: 'service_ids' });
  readonly closeEvent = output<any>({ alias: 'closeEvent' });

  services: any[] = [];
  couponServies: CouponServicesResponse[] = [];
  selectedServices = new BehaviorSubject<CouponServicesResponse[]>([]);

  reloadList: boolean = false;

  ngOnInit(): void {
    this.BindServiceCategory();
  }

  BindServiceCategory() {
    this.serviceCategoryService
      .getServiceCategories(this.tenantId())
      .subscribe({
        next: (res: any) => {
          if (res.items)
            this.services = res.items.filter(
              (value: any) =>
                value.status == Status.Active.toString() ||
                value.status == Status[Status.Active],
            );
          else this.services = [];
          this.getCouponEligibleService();
        },
        error: (error: any) => {},
      });
  }

  getCouponEligibleService() {
    const couponMaster = this.couponMaster();
    if (!couponMaster) {
      return;
    }
    this.coupnService
      .getEligibleService(this.tenantId(), couponMaster.id)
      .subscribe({
        next: (res: any) => {
          if (res.items) {
            this.couponServies = res.items as CouponServicesResponse[];
            this.couponServies.forEach((o) => {
              o.service_category_name = this.services.find(
                (f) => f.id == o.service_category_id,
              ).name_primary_language;
            });
            this.selectedServices.next(this.couponServies);
          } else {
            this.selectedServices.next([]);
            this.couponServies = [];
          }
        },
        error: (error: any) => {},
      });
  }

  addService(event: any) {
    let payload: AddServiceCategoryRequest = {} as AddServiceCategoryRequest;
    payload.service_category_ids = [];
    payload.plan_ids = [];
    payload.product_sku_id = [];
    payload.service_category_ids.push(event.value.id);
    const couponMaster = this.couponMaster();
    if (!couponMaster) {
      return;
    }
    this.coupnService
      .addEligibleService(this.tenantId(), couponMaster.id, payload)
      .subscribe({
        next: (res: any) => {
          this.translate
            .get('ADMIN.COUPON.ERRORS.SERVICE_ADDED')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.getCouponEligibleService();
          this.reloadList = true;
        },
        error: (error: any) => {
          this.handleToastError(error);
        },
      });
  }

  removeService(couponServicesResponse: CouponServicesResponse) {
    if (this.couponServies && this.couponServies.length == 1) {
      this.translate
        .get('ADMIN.COUPON.ERRORS.ONE_SERVICE_REQUIRED')
        .subscribe((translation) => {
          this.toastMessageService.showErrorMessage(translation);
        });
    } else {
      this.coupnService
        .removeEligibleService(
          this.tenantId(),
          couponServicesResponse.offer_master_id,
          couponServicesResponse.id,
        )
        .subscribe({
          next: (res: any) => {
            this.translate
              .get('ADMIN.COUPON.ERRORS.SERVICE_REMOVED')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.getCouponEligibleService();
            this.reloadList = true;
          },
          error: (error: any) => {
            this.handleToastError(error);
          },
        });
    }
  }

  handleToastError(error: any) {
    this.toastMessageService.showError(error);
  }

  closePopup() {
    this.closeEvent.emit({ reload: this.reloadList });
    this.closeEvent.emit({ reload: this.reloadList });
  }
}
